import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FileText, Shield, CreditCard, Check, X, Loader2, ChevronDown, ChevronUp, Users, Cookie } from 'lucide-react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { BACKEND_URL } from '../config/api';
import './TermsModal.css';

const API = `${BACKEND_URL}/api`;

const ALL_DOCS = ['terms_of_service', 'independent_contractor_agreement', 'payment_terms'];

const TermsModal = ({ isOpen, onAccept, onDecline, isUpdate = false, previousVersion = null, documentsToAccept = null }) => {
  const docsNeedingAcceptance = useMemo(() => documentsToAccept || ALL_DOCS, [documentsToAccept]);
  
  const [expanded, setExpanded] = useState(null);
  
  const initialAgreed = useMemo(() => {
    const result = {};
    ALL_DOCS.forEach(doc => { result[doc] = !docsNeedingAcceptance.includes(doc); });
    return result;
  }, [docsNeedingAcceptance]);
  
  const [agreed, setAgreed] = useState(initialAgreed);
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);
  const contentRefs = useRef({});
  const bodyRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchTerms();
      const newAgreed = {};
      ALL_DOCS.forEach(doc => { newAgreed[doc] = !docsNeedingAcceptance.includes(doc); });
      setAgreed(newAgreed);
      setExpanded(null);
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
    }
  }, [isOpen, docsNeedingAcceptance]);

  useEffect(() => {
    if (expanded && contentRefs.current[expanded]) {
      setTimeout(() => contentRefs.current[expanded]?.scrollTo({ top: 0, behavior: 'instant' }), 50);
    }
  }, [expanded]);

  const fetchTerms = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/terms/active`);
      setTerms(response.data);
    } catch (err) {
      setTerms({
        terms_of_service: "Terms of Service - Please contact support if this doesn't load.",
        independent_contractor_agreement: "Independent Contractor Agreement - Please contact support if this doesn't load.",
        payment_terms: "Payment Terms - Please contact support if this doesn't load.",
        version: "1.0"
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleAgreement = (id) => setAgreed(prev => ({ ...prev, [id]: !prev[id] }));
  const agreeToAll = () => setAgreed({ terms_of_service: true, independent_contractor_agreement: true, payment_terms: true });
  const allAgreed = Object.values(agreed).every(v => v);

  const handleAccept = async () => {
    try { await axios.post(`${API}/terms/consent`, {}, { withCredentials: true }); } catch (err) {}
    onAccept();
  };

  if (!isOpen) return null;

  const sections = [
    { id: 'terms_of_service', title: 'Terms of Service', icon: FileText, content: terms?.terms_of_service || '' },
    { id: 'independent_contractor_agreement', title: 'Contractor Agreement', icon: Users, content: terms?.independent_contractor_agreement || '' },
    { id: 'payment_terms', title: 'Payment Terms', icon: CreditCard, content: terms?.payment_terms || '' },
  ];

  return (
    <div className="tm-overlay" data-testid="terms-modal">
      <div className="tm-card">
        {/* Header */}
        <div className="tm-head">
          <h2>{isUpdate ? "Terms Updated" : "Welcome to taskeri®"}</h2>
          <p>{isUpdate ? "Please review changes to continue." : "Review and accept our terms to get started."}</p>
          {terms?.version && (
            <span className="tm-ver">
              {isUpdate && previousVersion ? `v${previousVersion} → v${terms.version}` : `v${terms.version}`}
            </span>
          )}
        </div>

        {loading ? (
          <div className="tm-loading"><Loader2 size={24} className="tm-spin" /><span>Loading...</span></div>
        ) : (
          <>
            {/* Sections */}
            <div className="tm-body" ref={bodyRef}>
              {sections.map(({ id, title, icon: Icon, content }) => {
                const needs = docsNeedingAcceptance.includes(id);
                const isOpen = expanded === id;
                return (
                  <div key={id} className={`tm-sec ${!needs ? 'tm-sec-done' : ''}`}>
                    <div className="tm-sec-row" onClick={() => setExpanded(isOpen ? null : id)}>
                      <div className="tm-sec-left">
                        <Icon size={16} strokeWidth={1.8} />
                        <span className="tm-sec-title">{title}</span>
                        {needs && isUpdate && <span className="tm-badge-upd">New</span>}
                      </div>
                      <div className="tm-sec-right">
                        <label className={`tm-check ${agreed[id] ? 'on' : ''}`} onClick={e => { e.stopPropagation(); if (needs) toggleAgreement(id); }}>
                          <span className="tm-check-box">{agreed[id] && <Check size={10} strokeWidth={3} />}</span>
                          <span className="tm-check-txt">{needs ? 'I agree' : 'Accepted'}</span>
                        </label>
                        {isOpen ? <ChevronUp size={16} className="tm-chev" /> : <ChevronDown size={16} className="tm-chev" />}
                      </div>
                    </div>
                    {isOpen && (
                      <div className="tm-sec-body" ref={el => { contentRefs.current[id] = el; if (el) el.scrollTo({ top: 0, behavior: 'instant' }); }}>
                        <div className="tm-html" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="tm-foot">
              {!allAgreed && (
                <button className="tm-btn-all" onClick={agreeToAll}>
                  <Check size={14} strokeWidth={2.5} /> Agree to All
                </button>
              )}
              <div className="tm-foot-links">
                Also see our <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> & <a href="/cookie-policy" target="_blank" rel="noopener noreferrer">Cookie Policy</a>
              </div>
              <div className="tm-foot-actions">
                <button className="tm-btn tm-btn-skip" onClick={onDecline}><X size={14} /> Decline</button>
                <button className="tm-btn tm-btn-go" onClick={handleAccept} disabled={!allAgreed}><Check size={14} /> Accept & Continue</button>
              </div>
            </div>
          </>
        )}
      </div>

    </div>
  );
};

export default TermsModal;
